import React from 'react';
import { ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';

const HERO_EASE = [0.16, 1, 0.3, 1];
const HERO_CONTENT_REVEAL = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.9, ease: HERO_EASE },
};
const HERO_IMAGE_REVEAL = {
  initial: { opacity: 0, scale: 1.05 },
  animate: { opacity: 1, scale: 1 },
  transition: { duration: 1.2, ease: HERO_EASE, delay: 0.15 },
};
const CTA_HOVER = {
  whileHover: { scale: 1.03 },
  whileTap: { scale: 0.98 },
  transition: { type: 'spring', stiffness: 400, damping: 12 },
};

// Hand-drawn nutrition doodles as inline SVG components
const CarrotDoodle = () => (
  <svg className="nutrition-doodle doodle-color-clay" width="80" height="100" viewBox="0 0 80 100" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M40 20 C40 20, 35 18, 33 22 L32 28 C32 28, 28 30, 26 34 L24 45 C24 45, 22 50, 24 58 L28 72 C28 72, 30 78, 34 82 C34 82, 38 86, 42 86 C42 86, 46 86, 50 82 C50 82, 54 78, 56 72 L60 58 C60 58, 62 50, 60 45 L58 34 C58 34, 56 30, 52 28 L51 22 C51 22, 49 18, 44 20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none"/>
    <path d="M38 20 L35 12 M42 20 L45 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

const AppleDoodle = () => (
  <svg className="nutrition-doodle doodle-color-green" width="70" height="80" viewBox="0 0 70 80" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M35 15 C35 15, 25 18, 20 28 C20 28, 15 38, 16 48 C16 48, 17 58, 22 64 C22 64, 28 72, 35 73 C35 73, 42 72, 48 64 C48 64, 53 58, 54 48 C54 48, 55 38, 50 28 C50 28, 45 18, 35 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none"/>
    <path d="M35 15 C35 15, 36 10, 38 8 L40 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M38 8 C38 8, 42 9, 45 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

const LeafDoodle = () => (
  <svg className="nutrition-doodle doodle-color-green" width="60" height="70" viewBox="0 0 60 70" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M10 60 C10 60, 15 50, 20 40 C20 40, 25 28, 32 20 C32 20, 38 12, 45 8 C45 8, 48 6, 50 6 C50 6, 48 10, 45 15 C45 15, 40 22, 35 32 C35 32, 30 42, 28 50 C28 50, 26 56, 25 60" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none"/>
    <path d="M32 20 C32 20, 38 22, 42 20 M28 32 C28 32, 34 34, 38 32" stroke="currentColor" strokeWidth="1" strokeLinecap="round"/>
  </svg>
);

export const HeroSection = () => {
  return (
    <section 
      className="section-padding-large grain-texture relative overflow-hidden"
      style={{ background: 'var(--brand-purple-bg)' }}
    >
      {/* Background Blobs */}
      <div className="blob-bg blob-purple animate-float-slow" style={{ width: '400px', height: '400px', top: '-100px', right: '10%' }}></div>
      <div className="blob-bg blob-clay animate-float-slow" style={{ width: '350px', height: '350px', bottom: '-80px', left: '5%' }}></div>
      
      {/* Subtle Nutrition Doodles */}
      <div style={{ position: 'absolute', top: '15%', left: '8%' }}>
        <LeafDoodle />
      </div>
      <div style={{ position: 'absolute', bottom: '20%', right: '5%', transform: 'rotate(15deg)' }}>
        <AppleDoodle />
      </div>

      <div className="container-custom relative z-10">
        <div className="grid lg:grid-cols-2 gap-20 items-center">
          {/* Left Content - Constrained Width with Increased Spacing */}
          <motion.div className="max-w-[580px]" {...HERO_CONTENT_REVEAL}>
            <div className="space-y-8">
              <h1 className="hero-large text-balance" style={{ maxWidth: '560px', color: '#FFFFFF' }}>
                Your body isn't built to resist temptation all day
              </h1>
              <p className="body-large" style={{ color: 'rgba(255, 255, 255, 0.92)', maxWidth: '520px' }}>
                Ice cream appears. Cravings follow. Willpower fades. 

Lasting health isn't about saying 'no' more often. It's about designing an ecosystem where fewer no's are required.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-start gap-5 mt-12">
              <motion.a
                href="https://wa.me/919766144233"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary"
                data-testid="hero-book-consultation-btn"
                {...CTA_HOVER}
              >
                Book Your 1:1 Consultation
                <ArrowRight size={20} />
              </motion.a>
              <a
                href="#programs"
                className="btn-secondary"
                data-testid="hero-view-programs-btn"
                style={{ opacity: 0.95, color: '#FFFFFF', borderColor: 'rgba(255, 255, 255, 0.85)' }}>
                View Programs
              </a>
            </div>

            {/* Micro Strip Below CTAs */}
            <div className="mt-8 text-center sm:text-left">
              <p className="text-sm" style={{ color: 'rgba(255, 255, 255, 0.75)', fontWeight: 500 }}>
                Personalised Nutrition for modern lives
              </p>
            </div>
          </motion.div>

          {/* Right Image - Floating Card with Blob Behind */}
          <motion.div className="flex justify-center lg:justify-end relative" {...HERO_IMAGE_REVEAL}>
            {/* Blob Behind Image */}
            <div 
              className="absolute blob-bg blob-green" 
              style={{ 
                width: '420px', 
                height: '420px', 
                top: '20px', 
                right: '-40px',
                filter: 'blur(60px)',
                opacity: 0.08
              }}
            ></div>
            
            {/* Floating Image Container */}
            <div className="floating-image max-w-[520px] w-full animate-float relative z-10">
              <img
                src="https://customer-assets.emergentagent.com/job_arogya-vitalis/artifacts/km793xqr_ChatGPT%20Image%20Feb%202%2C%202026%2C%2011_39_50%20PM.png"
                alt="Creating a healthy ecosystem - the Arogya Vitalis approach"
                className="w-full h-auto object-cover"
              />
            </div>

            {/* Small Doodle Near Image */}
            <div style={{ position: 'absolute', top: '8%', left: '-5%', opacity: 0.08 }}>
              <CarrotDoodle />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
